import React from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { alpha } from '@mui/system/colorManipulator';
import TooltipCheckpoint from '#component/map/tooltip-checkpoint';
import { getContrastColor } from '#component/theme';

function addHover() {
  const { map } = this;
  const { mapboxgl } = window;
  const { account, theme, destinations } = map.app;
  // Create a popup, but don't add it to the map yet.
  const popup = new mapboxgl.Popup({
    offset: 12,
    closeButton: false,
    closeOnClick: false,
    maxWidth: 320
  });

  const div = document.createElement('div');
  const root = createRoot(div);

  map.on('mouseenter', 'checkpoints', e => {
    // Change the cursor style as a UI indicator.
    map.getCanvas().style.cursor = 'pointer';

    // Copy coordinates array.
    const coordinates = e.features[0].geometry.coordinates.slice();
    const { properties } = e.features[0];
    root.render(
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <TooltipCheckpoint
            checkpoint={destinations[properties.number - 1]}
            index={properties.number - 1}
            checkpoints={destinations}
            account={account}
          />
        </ThemeProvider>
      </StyledEngineProvider>
    );

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    // Populate the popup and set its coordinates
    // based on the feature found.
    popup.setLngLat(coordinates).setDOMContent(div).addTo(map);
  });
  map.on('mouseleave', 'checkpoints', () => {
    map.getCanvas().style.cursor = '';
    // prevents jumping on load
    setTimeout(() => root.unmount());
    popup.remove();
  });
}

function getGeoJSON(data) {
  const features = [];
  data.forEach((d, i) => {
    if (d.locations) {
      features.push({
        type: 'Feature',
        properties: {
          id: d.id,
          done: Boolean(d.done),
          number: i + 1,
          lng: d.locations[0].lng,
          lat: d.locations[0].lat,
          radius: d.locations[0].radius
        },
        geometry: {
          type: 'Point',
          coordinates: [d.locations[0].lng, d.locations[0].lat]
        }
      });
    }
  });

  return {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features
    }
  };
}

export default {
  addLayer(map) {
    this.map = map;
    const { theme } = map.app;

    map.addLayer({
      id: 'destinations-radius',
      source: 'destinations',
      type: 'circle',
      paint: {
        'circle-color': ['case', ['get', 'done'], alpha(getContrastColor(), 0.5), '#c5c5c5'],
        'circle-radius': [
          'interpolate',
          ['exponential', 2],
          ['zoom'],
          0, 0,
          20, ['/',
            ['/', ['get', 'radius'], ['/', 78271.484, ['^', 2, 20]]],
            ['cos', ['*', ['get', 'lat'], ['/', Math.PI, 180]]]
          ]],
        'circle-opacity': 0.5
      }
    });

    map.addLayer({
      id: 'checkpoints',
      source: 'destinations',
      type: 'circle',
      paint: {
        'circle-color': ['case', ['get', 'done'], getContrastColor(), '#e0e0e0'],
        'circle-radius': 8,
        'circle-stroke-width': 2,
        'circle-stroke-color': ['case', ['==', ['get', 'highlight'], true], theme.palette.getContrastText(getContrastColor()), '#757575']
      },
      layout: {
        'circle-sort-key': ['+', ['case', ['==', ['get', 'highlight'], true], 1000, ['get', 'number']]]
      }
    });
    map.addLayer({
      id: 'checkpoints-text',
      source: 'destinations',
      type: 'symbol',
      layout: {
        'text-field': '{number}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
        // for some reasons `checkpoints-text` has different render order
        'symbol-sort-key': ['-', ['case', ['==', ['get', 'highlight'], true], 1000, ['get', 'number']]],
        'text-padding': 0,
        'text-allow-overlap': true
      },
      paint: {
        'text-color': ['case', ['get', 'done'], theme.palette.getContrastText(getContrastColor()), '#000']
      }
    });
  },
  getGeoJSON,
  addHover,
  highlight(checkpoint, mouseenter) {
    const { map } = this;
    if (map) {
      const geojson = getGeoJSON(map.app.destinations);
      if (mouseenter) {
        const current = geojson.data.features.find(f => f.properties.id === checkpoint.id);
        current.properties.highlight = mouseenter;
      }
      map.getSource('destinations').setData(geojson.data);
    }
  },
  click(lat, lng) {
    const { map } = this;
    if (map) {
      map.fitBounds([lng, lat, lng, lat], { padding: 50, maxZoom: 15 });
    }
  }
};
